import { cn } from '@/lib/utils'
import { REQUEST_STATUS_LABELS, type RequestStatus } from '@/hooks/useProductRequests'

// Statuses are grouped by whose move it is: amber = we need to act, blue = in
// progress, teal = in transit, green = arrived/done, grey = closed. Colours are
// the shared --status-* token pairs (same as the PO StatusBadge); see
// docs/designs/colour-pass.md.
const AWAITING = 'bg-[var(--status-awaiting-bg)] text-[var(--status-awaiting-fg)]'
const DRAFT = 'bg-[var(--status-draft-bg)] text-[var(--status-draft-fg)]'
const ORDERED = 'bg-[var(--status-ordered-bg)] text-[var(--status-ordered-fg)]'
const APPROVED = 'bg-[var(--status-approved-bg)] text-[var(--status-approved-fg)]'
const ARCHIVED = 'bg-[var(--status-archived-bg)] text-[var(--status-archived-fg)]'

const STATUS_CONFIG: Record<RequestStatus, { label: string; className: string }> = {
  OPEN: { label: REQUEST_STATUS_LABELS.OPEN, className: AWAITING },
  NEED_TO_SOURCE: { label: REQUEST_STATUS_LABELS.NEED_TO_SOURCE, className: AWAITING },
  SOURCED: { label: REQUEST_STATUS_LABELS.SOURCED, className: DRAFT },
  ADDED_TO_PO: { label: REQUEST_STATUS_LABELS.ADDED_TO_PO, className: DRAFT },
  ORDERED: { label: REQUEST_STATUS_LABELS.ORDERED, className: ORDERED },
  CUSTOMER_NOTIFIED: { label: REQUEST_STATUS_LABELS.CUSTOMER_NOTIFIED, className: APPROVED },
  PURCHASED: { label: REQUEST_STATUS_LABELS.PURCHASED, className: APPROVED },
  CLOSED_NO_PURCHASE: { label: REQUEST_STATUS_LABELS.CLOSED_NO_PURCHASE, className: ARCHIVED },
}

const BASE =
  'inline-block rounded-full text-[10.5px] font-semibold px-2 py-0.5 leading-none'

interface RequestStatusBadgeProps {
  status: string
  className?: string
}

export function RequestStatusBadge({ status, className }: RequestStatusBadgeProps) {
  const config = STATUS_CONFIG[status as RequestStatus] ?? {
    label: status,
    className: ARCHIVED,
  }

  return (
    <span className={cn(BASE, config.className, className)} data-status={status}>
      {config.label}
    </span>
  )
}

interface DemandBadgeProps {
  count: number
  className?: string
}

/**
 * "N requests" — flags a variant several customers have asked for. That is a
 * real buying signal (reorder, probably more than one), so it is amber
 * attention via the awaiting pair, never burgundy (identity only). It sits
 * next to the product title rather than in the Status column.
 */
export function DemandBadge({ count, className }: DemandBadgeProps) {
  return (
    <span
      className={cn(
        BASE,
        'bg-[var(--status-awaiting-bg)] text-[var(--status-awaiting-fg)]',
        className
      )}
      title={`${count} customers have requested this`}
    >
      {count} requests
    </span>
  )
}
